import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from '../utils/cn';
import { controlHeights } from './KoobInputText';
import KoobLoader from './KoobLoader';

// The UI kit's buttons. The main action is orange, always with navy text
// (white on orange fails contrast); `navy` is the kit's dark button.
export const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap font-semibold cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-navy-100 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      rounded: {
        sm: 'rounded-md',
        md: 'rounded-[10px]',
        full: 'rounded-full',
      },
      variant: {
        primary:
          'border-none bg-k-orange text-k-h hover:bg-orange-600 disabled:bg-navy-100 disabled:text-navy-400',
        navy: 'border-none bg-k-h text-white hover:bg-navy-700 disabled:bg-navy-100 disabled:text-navy-400',
        black:
          'border-none bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50',
        secondary:
          'border-[1.5px] border-solid border-navy-200 bg-white text-k-h hover:border-navy-300 hover:bg-navy-50 disabled:opacity-50',
        danger:
          'border-none bg-red-700 text-white hover:bg-red-800 disabled:bg-navy-100 disabled:text-navy-400',
        /** Destructive but secondary: red outline on a white button. */
        dangerOutline:
          'border-[1.5px] border-solid border-red-200 bg-white text-red-700 hover:border-red-300 hover:bg-red-50 disabled:opacity-50',
        success:
          'border-none bg-green-700 text-white hover:bg-green-800 disabled:opacity-50',
        ghost:
          'border-none bg-transparent text-k-h hover:bg-navy-50 disabled:opacity-50',
        // Quiet secondary actions: icon rows under a message, toolbars.
        subtle:
          'border-none bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50',
        soft: 'border-none bg-orange-50 text-orange-700 hover:bg-orange-100 disabled:opacity-50',
        outline:
          'border-[1.5px] border-solid border-k-h bg-white text-k-h hover:bg-k-h hover:text-white disabled:opacity-50',
        /** Light navy tint on hover, focus and while its menu is open. */
        brandOutline:
          'border-[1.5px] border-solid border-k-h bg-white text-k-h hover:bg-navy-50 focus-visible:bg-navy-50 data-[state=open]:bg-navy-50 disabled:opacity-50',
      },
      // sm and md line up with inputs in form rows (controlHeights); lg is
      // for a standalone call to action.
      size: {
        sm: `${controlHeights.sm} px-3.5 text-[13px] gap-1.5`,
        md: `${controlHeights.md} px-5 text-sm gap-2.5`,
        // Square, for an icon alone (give it an aria-label).
        icon: 'size-8 p-0 text-sm',
        lg: 'h-12 px-6 text-[15px] gap-2.5',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'md',
      rounded: 'md',
    },
  },
);

type KoobButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'className'
> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    block?: boolean;
    loading?: boolean;
    /** @deprecated use variant */
    color?: VariantProps<typeof buttonVariants>['variant'];
    /** @deprecated use data-testid */
    testId?: string;
  };

const KoobButton = React.forwardRef<HTMLButtonElement, KoobButtonProps>(
  (buttonProps, ref) => {
    const {
      variant,
      color,
      size,
      rounded,
      block = false,
      loading = false,
      asChild = false,
      disabled,
      testId,
      // className is not part of the API (see packages/ui/README.md) but is
      // still stripped here so a stray one can't clobber the computed classes
      className,
      children,
      // ponytail: default 'button' (not shadcn's implicit 'submit') — legacy
      // call sites sit inside forms and always pass type="submit" explicitly
      type = 'button',
      ...props
    } = buttonProps as typeof buttonProps & { className?: string };
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type}
        className={cn(
          buttonVariants({ variant: variant ?? color, size, rounded }),
          block && 'w-full',
          loading && 'relative',
          // :disabled doesn't match <a>; block Slot-rendered links by hand
          // ponytail: pointer-events only — keyboard activation not blocked
          asChild && (disabled || loading) && 'pointer-events-none opacity-50',
        )}
        aria-disabled={asChild && (disabled || loading) ? true : undefined}
        aria-busy={loading || undefined}
        disabled={asChild ? undefined : disabled || loading}
        data-testid={testId}
        {...props}
      >
        {/* asChild (Slot) requires a single child: pass children through
            untouched and never emit sibling nodes (even falsy ones — a
            `{false}` sibling makes Slot count >1 child and throw). */}
        {asChild ? (
          children
        ) : (
          <>
            {loading && (
              <span className="absolute inset-0 flex items-center justify-center">
                <KoobLoader color="currentColor" size={16} />
              </span>
            )}
            {/* keep children in flow so width is preserved; hide while loading */}
            {loading ? (
              <span className="invisible contents">{children}</span>
            ) : (
              children
            )}
          </>
        )}
      </Comp>
    );
  },
);

KoobButton.displayName = 'KoobButton';

export default KoobButton;
